import { marked, type Tokens } from "marked";

export interface MarkdownTextSegment {
  type: "text";
  /** Position in the answer — stable as the stream appends to the end. */
  id: string;
  markdown: string;
}

export interface MarkdownTableRow {
  /** Keeps a row's identity across a re-sort. */
  id: string;
  cells: string[];
}

export interface MarkdownTableSegment {
  type: "table";
  id: string;
  align: Array<"center" | "left" | "right" | null>;
  header: string[];
  rows: MarkdownTableRow[];
}

export type MarkdownSegment = MarkdownTextSegment | MarkdownTableSegment;

const INLINE_LINK = /!?\[([^\]]*)\]\([^)]*\)/g;
const INLINE_MARKS = /[*_`~]/g;

export const markdownCellText = (cell: string): string =>
  cell.replace(INLINE_LINK, "$1").replace(INLINE_MARKS, "").trim();

export const parseMarkdownSegments = (markdown: string): MarkdownSegment[] => {
  const tokens = marked.lexer(markdown, { gfm: true, breaks: true });
  const segments: MarkdownSegment[] = [];
  let text = "";

  const flushText = () => {
    if (text)
      segments.push({
        type: "text",
        id: `text-${segments.length}`,
        markdown: text,
      });
    text = "";
  };

  for (const token of tokens) {
    if (token.type !== "table") {
      text += token.raw;
      continue;
    }

    const table = token as Tokens.Table;
    flushText();
    segments.push({
      type: "table",
      id: `table-${segments.length}`,
      align: table.align,
      header: table.header.map((cell) => cell.text),
      rows: table.rows.map((row, index) => ({
        id: `row-${index}`,
        cells: row.map((cell) => cell.text),
      })),
    });
  }

  flushText();

  return segments;
};
